import React, { memo, useMemo, useState, useEffect } from 'react';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import Tooltip from '@mui/material/Tooltip';

import { VIP } from '../../../constants/strings';
import SimpleModal from '../../SimpleModal';

import palette from '../../styles/palette';
import useLocationQueryParams from '../../../hooks/useLocationQueryParams';

const useStyles = makeStyles(() => ({
  modalRoot: {
    '& .MuiDialog-paperWidthSm': {
      maxWidth: '80% !important',
    },
  },
}));

const getParcelInfoLabel = (key) => {
  switch (key) {
    case 'udprn':
      return 'UDPRN+Suffix';
    case 'highValue':
      return 'High Value';
    case 'idRequired':
      return 'ID Required';
    case 'vip':
      return VIP;
    case 'propertyType':
      return 'Property Type';
    default:
      return key.charAt(0).toUpperCase() + key.slice(1);
  }
};

export const ParcelsInfoModal = ({ open, onClose, stop }) => {
  const classes = useStyles();
  const [order, setOrder] = useState('asc');
  const title = `Parcels Info - Stop ${stop.displayNumber}`;
  const { detailVisible } = useLocationQueryParams();

  useEffect(() => {
    if (open && !detailVisible) {
      onClose();
    }
  }, [open, detailVisible, onClose]);

  const rowKeys = useMemo(
    () => Object.keys(stop.parcelsInfo),
    [stop.parcelsInfo]
  );

  const parcelNumbers = useMemo(
    () => Object.keys(stop.parcelsInfo[rowKeys[0]]),
    [stop.parcelsInfo, rowKeys]
  );

  const handleSort = () => {
    setOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
  };

  const sortedRowKeys = [...rowKeys].sort((a, b) => {
    const labelA = getParcelInfoLabel(a).toLowerCase();
    const labelB = getParcelInfoLabel(b).toLowerCase();
    if (order === 'asc') {
      return labelA.localeCompare(labelB);
    }
    return labelB.localeCompare(labelA);
  });

  return (
    <SimpleModal
      onClose={onClose}
      open={open}
      title={title}
      rootClassName={classes.modalRoot}
    >
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell
                sx={{
                  position: 'sticky',
                  left: 0,
                  background: palette.typography.white.solid,
                  zIndex: 2,
                  minWidth: 100,
                  fontSize: '12px',
                  color: palette.dpdBlack,
                  letterSpacing: '0.11256px',
                  lineHeight: '18px',
                }}
                align="left"
              >
                <TableSortLabel
                  active
                  direction={order}
                  onClick={handleSort}
                  sx={{
                    '& .MuiTableSortLabel-icon': {
                      fontSize: '14px',
                      color: palette.dpdBlack,
                    },
                  }}
                >
                  Parcel Info
                </TableSortLabel>
              </TableCell>
              {parcelNumbers.map((parcelNum) => (
                <TableCell
                  key={parcelNum}
                  align="center"
                  sx={{
                    fontSize: '12px',
                    color: palette.dpdBlack,
                    letterSpacing: '0.11256px',
                    lineHeight: '18px',
                  }}
                >
                  {parcelNum}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedRowKeys.map((rowKey) => (
              <TableRow key={rowKey}>
                <TableCell
                  component="th"
                  scope="row"
                  sx={{
                    position: 'sticky',
                    left: 0,
                    background: palette.typography.white.solid,
                    zIndex: 1,
                    fontSize: '12px',
                    color: palette.dpdBlack,
                    letterSpacing: '0.11256px',
                    lineHeight: '18px',
                  }}
                >
                  {getParcelInfoLabel(rowKey)}
                </TableCell>
                {parcelNumbers.map((parcelNum) => (
                  <TableCell
                    key={parcelNum + rowKey}
                    align="center"
                    sx={{
                      fontSize: '12px',
                      color: palette.dpdBlack,
                      letterSpacing: '0.11256px',
                      lineHeight: '18px',
                    }}
                  >
                    {String(stop.parcelsInfo[rowKey][parcelNum])}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </SimpleModal>
  );
};

const StopInfoItem = ({ value }) => {
  const tooltipContent = useMemo(() => {
    if (!value) {
      return null;
    }
    return Object.keys(value)
      .filter((key) => value[key] !== undefined)
      .sort((a, b) => {
        const labelA = getParcelInfoLabel(a).toLowerCase();
        const labelB = getParcelInfoLabel(b).toLowerCase();
        return labelA.localeCompare(labelB);
      })
      .map((key) => `${getParcelInfoLabel(key)}: ${String(value[key])}`)
      .join('\n');
  }, [value]);

  return tooltipContent ? (
    <Tooltip
      title={<pre style={{ margin: 0 }}>{tooltipContent}</pre>}
      placement="top"
      arrow
    >
      <InfoOutlinedIcon
        style={{
          cursor: 'pointer',
          width: '18px',
          height: '18px',
          color: palette.typography.white.solid,
        }}
      />
    </Tooltip>
  ) : (
    <InfoOutlinedIcon
      style={{
        cursor: 'pointer',
        width: '18px',
        height: '18px',
        color: palette.dpdBlack,
      }}
    />
  );
};

export default memo(StopInfoItem);
